
import { CONTESTS_STATES } from "../../constants/contests";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import classNames from "classnames";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface ContestCardProps {
  contest: any; // Type this properly based on your Contest type
  onClick: () => void;
  timezone?: string;
}

// Define contest state type to match the CONTESTS_STATES keys
type ContestState = keyof typeof CONTESTS_STATES;

const getEffectiveTimezone = (timezone?: string) => (
  timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
);

const toZonedDate = (value: string | undefined, timezone: string) => {
  if (!value) return null;

  const zonedDate = dayjs.utc(value).tz(timezone);

  if (!zonedDate.isValid()) {
    return null;
  }

  return zonedDate;
};

// Helper function to calculate progress and days left based on contest state
const getStateTimeInfo = (
  state: ContestState | undefined,
  calendar: any,
  insertedAt: string,
  timezone?: string,
) => {
  const resolvedTimezone = getEffectiveTimezone(timezone);
  const now = dayjs().tz(resolvedTimezone);
  let progress = 0;
  let daysLeft = 0;
  let targetDate = null;

  if (!state) return { progress, daysLeft };

  switch (state) {
    case 'published':
      // Progress until registration starts
      targetDate = toZonedDate(calendar?.registrationStartsAt, resolvedTimezone);
      if (targetDate) {
        const startDate = toZonedDate(insertedAt, resolvedTimezone);
        if (!startDate) break;

        const totalDays = Math.max(1, targetDate.diff(startDate, 'day'));
        const passedDays = now.diff(startDate, 'day');
        progress = Math.min(100, (passedDays / totalDays) * 100);
        daysLeft = targetDate.diff(now, 'day');
      }
      break;

    case 'registering':
      // Progress until registration ends
      targetDate = toZonedDate(calendar?.registrationEndsAt, resolvedTimezone);
      if (targetDate) {
        const startDate = toZonedDate(calendar?.registrationStartsAt, resolvedTimezone);
        if (!startDate) break;

        const totalDays = Math.max(1, targetDate.diff(startDate, 'day'));
        const passedDays = now.diff(startDate, 'day');
        progress = Math.min(100, (passedDays / totalDays) * 100);
        daysLeft = targetDate.diff(now, 'day');
      }
      break;

    case 'storing_entries':
      // Progress until storing ends
      targetDate = toZonedDate(calendar?.storingEndsAt, resolvedTimezone);
      if (targetDate) {
        const startDate = toZonedDate(calendar?.storingStartsAt, resolvedTimezone);
        if (!startDate) break;

        const totalDays = Math.max(1, targetDate.diff(startDate, 'day'));
        const passedDays = now.diff(startDate, 'day');
        progress = Math.min(100, (passedDays / totalDays) * 100);
        daysLeft = targetDate.diff(now, 'day');
      }
      break;

    case 'in_progress':
      // Progress until contest ends
      targetDate = toZonedDate(calendar?.contestEndsAt, resolvedTimezone);
      if (targetDate) {
        const startDate = toZonedDate(calendar?.contestStartsAt, resolvedTimezone);
        if (!startDate) break;

        const totalDays = Math.max(1, targetDate.diff(startDate, 'day'));
        const passedDays = now.diff(startDate, 'day');
        progress = Math.min(100, (passedDays / totalDays) * 100);
        daysLeft = targetDate.diff(now, 'day');
      }
      break;

    case 'finished':
      // Progress until results reveal
      targetDate = toZonedDate(calendar?.resultsRevealAt, resolvedTimezone);
      if (targetDate) {
        const startDate = toZonedDate(calendar?.contestEndsAt, resolvedTimezone);
        if (!startDate) break;

        const totalDays = Math.max(1, targetDate.diff(startDate, 'day'));
        const passedDays = now.diff(startDate, 'day');
        progress = Math.min(100, (passedDays / totalDays) * 100);
        daysLeft = targetDate.diff(now, 'day');
      }
      break;

    default:
      progress = 0;
      daysLeft = 0;
  }

  return { progress, daysLeft };
};

export const ContestCard = ({ contest, onClick, timezone }: ContestCardProps) => {
  const [t] = useTranslation();
  const [logoError, setLogoError] = useState(false);
  const currentEdition = contest?.currentEdition?.data || contest?.contestsEditions?.data?.[0];

  const logo = currentEdition?.logo;
  const showPlaceholder = !logo || logoError;

  // Calculate registration progress
  const state = currentEdition?.state as ContestState | undefined;
  const { progress, daysLeft } = getStateTimeInfo(
    state,
    currentEdition?.calendar,
    currentEdition?.insertedAt,
    timezone,
  );

  return (
    <div
      onClick={onClick}
      className="relative col-span-12 grid cursor-pointer grid-cols-12 gap-4 overflow-hidden rounded-xl border-4 border-solid lg:col-span-6 2xl:col-span-3 bg-base-50 dark:bg-content-800 border-content-200/10 dark:border-content-700/10 shadow-xl shadow-content-900/5 dark:shadow-content-900/20 hover:scale-105 transition-transform"
    >
      <div className="col-span-5 h-36">
        {showPlaceholder ? (
          <div className="flex h-full w-full items-center justify-center bg-content-100/50 dark:bg-content-700/50">
            <div className="icon-[ph--trophy] text-primary-500 dark:text-primary-400 h-24 w-24" />
          </div>
        ) : (
          <img
            src={logo}
            onError={() => setLogoError(true)}
            alt={contest.name}
            className="h-full w-full object-cover"
          />
        )}
      </div>

      <div className="font-sans col-span-7 flex flex-col justify-between py-2 pl-1">
        <div>
          <div data-cy="contest-card-name" className="font-header text-content-800 dark:text-content-200 text-xl font-medium">
            {contest.name}
          </div>
          <div className="text-primary-600 dark:text-primary-400 mb-1 text-lg uppercase font-medium">
            {state && CONTESTS_STATES[state] ? t(CONTESTS_STATES[state].label) : ''}
          </div>
          <div className="text-content-500 dark:text-content-400 text-sm font-medium">
            {currentEdition?.location?.city} / {currentEdition?.location?.country}
          </div>
        </div>

        {currentEdition?.entriesCount > 0 && currentEdition?.contestType !== 'commercial' && (
          <div className="mt-2 flex items-center gap-2">
            <div className="icon-[ph--beer-bottle] text-content-400 dark:text-content-500 h-4 w-4" />
            <span className="text-content-600 dark:text-content-300 text-sm font-medium">
              {currentEdition.entriesCount} {t('global:shared.entries', 'entries')}
            </span>
          </div>
        )}
      </div>
      {(state === 'published' || state === 'registering' || state === 'storing_entries' || state === 'in_progress' || state === 'finished') && daysLeft > 0 && (
        <div className="absolute bottom-0 left-0 right-0">
          <div className="relative">
            {/* Days left indicator */}
            <div className="absolute -top-6 left-3 font-sans text-sm">
              <span className="text-content-500 dark:text-content-400">
                {state === 'published' && t('global:shared.registrationStartsIn', 'Registration starts in ')}
                {state === 'registering' && t('global:shared.registrationEndsIn', 'Registration ends in ')}
                {state === 'storing_entries' && t('global:shared.entriesEndsIn', 'Storing entries ends in ')}
                {state === 'in_progress' && t('global:shared.inProgressEndsIn', 'Judging ends in ')}
                {state === 'finished' && t('global:shared.resultsRevealIn', 'Results reveal in ')}
              </span>
              <span className={classNames(
                "font-medium",
                state === 'published'
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-success-600 dark:text-success-400"
              )}>
                {' '}{daysLeft} {t('global:shared.days', 'days')}
              </span>
            </div>

            {/* Progress bar with shimmer */}
            <div className="h-1 w-full bg-content-200/20 dark:bg-content-700/20 overflow-hidden">
              <div
                className={classNames(
                  "h-full transition-all duration-500 relative",
                  state === 'published'
                    ? "bg-primary-500/50 dark:bg-primary-400/50"
                    : "bg-success-500/50 dark:bg-success-400/50",
                  "after:absolute after:inset-0",
                  "after:bg-gradient-to-r after:from-transparent after:via-white/20 after:to-transparent",
                  "after:animate-shimmer after:translate-x-[-100%]"
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
